import type { MenuCategory, MenuItem } from '@/models'

type Errors = {
  footer: string[]
  header: string[]
  mobileMenu: string[]
}

export const useNavigationStore = defineStore('navigation.store', () => {
  const { updateNavigation } = useNavigations()

  const footerMenuCategories = ref<MenuCategory[]>([])
  const headerMenu = ref<Undefinable<MenuItem[]>>(undefined)
  const headerMenuExtended = ref<Undefinable<MenuItem[]>>(undefined)
  const mobileMenuItems = ref<Undefinable<MenuItem[]>>(undefined)
  const isMobileMenuVisible = ref<boolean>(false)
  const errors = ref<Errors>({
    footer: [],
    header: [],
    mobileMenu: [],
  })

  const headerErrors = computed(() => errors.value.header)
  // TODO: headerNav и headerNavExtended содержат одинаковую логику, но обрабатывают разные массивы. После перехода на setup-стор можно избавиться от дублирования кода
  const headerNav = computed(() => {
    if (headerMenu.value) {
      return headerMenu.value
        .reduce<MenuItem[]>((group, item) => {
          const headerItem: MenuItem = {
            ...item,
            // TODO: сейчас все роуты в меню внешние
            inner: false,
          }
          const headerItemSubmenu = headerMenu.value!.filter(
            (submenuItem) => submenuItem.parentId === item.id,
          )

          if (headerItemSubmenu.length) {
            headerItem.submenu = headerItemSubmenu
          }
          if (!item.parentId) {
            group.push(headerItem)
          }

          return group
        }, [])
        .sort((itemA, itemB) => {
          if (itemA.submenu && !itemB.submenu) return 1
          if (itemB.submenu && !itemA.submenu) return -1

          if (itemA.class && !itemB.class) return 1
          if (!itemA.class && itemB.class) return -1

          return 0
        })
    }

    return []
  })
  const headerNavExtended = computed(() => {
    if (headerMenuExtended.value) {
      return headerMenuExtended.value
        .reduce<MenuItem[]>((group, item) => {
          const headerItem: MenuItem = {
            ...item,
            // TODO: сейчас все роуты в меню внешние
            inner: false,
          }
          const headerItemSubmenu = headerMenuExtended.value!.filter(
            (submenuItem) => submenuItem.parentId === item.id,
          )

          if (headerItemSubmenu.length) {
            headerItem.submenu = headerItemSubmenu
          }
          if (!item.parentId) {
            group.push(headerItem)
          }

          return group
        }, [])
        .sort((itemA, itemB) => {
          if (itemA.submenu && !itemB.submenu) return 1
          if (itemB.submenu && !itemA.submenu) return -1

          if (itemA.class && !itemB.class) return 1
          if (!itemA.class && itemB.class) return -1

          return 0
        })
    }

    return []
  })

  const headerNavFlat = computed((): MenuItem[] => {
    return (headerNav.value as MenuItem[])?.reduce<MenuItem[]>((acc, item) => {
      if (item.submenu) {
        acc.push(...item.submenu)
      } else {
        acc.push(item)
      }

      return acc
    }, [])
  })
  const mobileNav = computed(() => mobileMenuItems.value || [])

  const setFooterMenuNav = (payload: MenuCategory[]) => {
    footerMenuCategories.value = payload.map((category) => {
      return {
        ...category,
        items:
          category.items?.map((navItem) => {
            return {
              ...navItem,
              // TODO: сейчас все роуты в меню внешние
              inner: false,
            }
          }) || [],
      }
    })
    errors.value.footer = []
  }

  const pinnedHeaderMenuItems = computed(() => {
    return headerNavFlat.value?.filter((item) => item.isPinned) || []
  })

  const setHeaderMenu = (payload: MenuItem[]) => {
    headerMenu.value = deleteDuplicateInArray(payload, ['href', 'isPinned'])
    errors.value.header = []
  }

  const setHeaderMenuExtended = (payload: MenuItem[]) => {
    headerMenuExtended.value = deleteDuplicateInArray(payload, [
      'title',
      'href',
    ])
  }

  /**
   * проверяет на наличие сервисных классов, которые надо прятать в подменю тоже
   * @param itemClass
   */
  const checkIsServiceNavClass = (itemClass: string) => {
    return (
      itemClass.includes('visible-xs') ||
      itemClass.includes('hidden-xs') ||
      itemClass.includes('bg-error-500')
    )
  }

  /**
   * функцию добавляет или удаляет пункты меню в/из подменю
   * @param length длина первого уровня меню
   */
  const updateHeaderNavOnResize = (length: number) => {
    const currentDefaultItems = headerNav.value!.filter((item) => !item.submenu)
    const submenu = headerNav.value.find((item) => item.submenu)

    if (headerMenu.value) {
      headerMenu.value = updateNavigation(
        headerMenu.value,
        currentDefaultItems,
        submenu,
        length,
      )
    }
  }

  /**
   * функцию добавляет или удаляет пункты меню в/из подменю
   * @param length длина первого уровня меню
   */
  const updateHeaderNavExtOnResize = (length: number) => {
    const currentDefaultItems = headerNavExtended.value!.filter(
      (item) => !item.submenu,
    )
    const submenu = headerNavExtended.value.find((item) => item.submenu)

    if (headerMenuExtended.value) {
      headerMenuExtended.value = updateNavigation(
        headerMenuExtended.value,
        currentDefaultItems,
        submenu,
        length,
      )
    }
  }

  const setMobileMenu = (payload: MenuItem[]) => {
    mobileMenuItems.value = deleteDuplicateInArray(payload, ['title', 'href'])
    errors.value.mobileMenu = []
  }

  const showMobileMenu = () => {
    $lockScroll()
    isMobileMenuVisible.value = true
  }

  const hideMobileMenu = () => {
    $unlockScroll()
    isMobileMenuVisible.value = false
  }

  return {
    footerMenuCategories,
    headerMenu,
    headerMenuExtended,
    mobileMenuItems,
    isMobileMenuVisible,
    errors,
    headerErrors,
    headerNavExtended,
    headerNavFlat,
    headerNav,
    mobileNav,
    pinnedHeaderMenuItems,
    setFooterMenuNav,
    setHeaderMenu,
    setHeaderMenuExtended,
    checkIsServiceNavClass,
    updateHeaderNavOnResize,
    updateHeaderNavExtOnResize,
    setMobileMenu,
    showMobileMenu,
    hideMobileMenu,
  }
})
